import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useAuth } from '@/hooks/useAuth'

/**
 * The organisation's branding, as every signed-in user sees it (owner checklist item 7, E-UX):
 * logo + name in the sidebar, and the accent colour on links, focus rings and selected states.
 * The accent is applied only when it keeps text readable (≥ 4.5:1 on white, WCAG 1.4.3); a
 * pale brand colour falls back to the design-system accent instead of making links unreadable.
 * Dark mode keeps its own accent (spec 20 §3.3.3).
 */
export interface OrgBranding { name: string | null; logo_url: string | null; accent_color: string | null; document_template: string }

export function useOrgBranding() {
  const { user } = useAuth()
  return useQuery({
    queryKey: ['me', 'branding'],
    queryFn: async () => (await api.get('/api/v1/me/branding')).data as OrgBranding,
    enabled: !!user,
    staleTime: 5 * 60_000,
    retry: false,
    meta: { inlineError: true },
  })
}

function channel(c: number) { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4 }
export function contrastOnWhite(hex: string): number {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)
  if (!m) return 0
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16))
  const L = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
  return 1.05 / (L + 0.05)
}

/**
 * The organisation accent is now resolved with the platform theme in `PreferencesProvider`
 * (themeEngine.resolveAppearance: platform -> organisation if allowed -> user if allowed), so this
 * component no longer writes colours itself; it stays mounted for the branding query warm-up.
 */
export function BrandingApplier() {
  useOrgBranding()
  return null
}
